import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import SignatureCanvas from "react-signature-canvas";

const SafetyResponsiveSignatureCanvas = forwardRef(
  ({ height = 220, penColor = "black", className = "", canvasClassName = "" }, ref) => {
    const signatureContainerRef = useRef(null);
    const sigCanvasRef = useRef(null);
    const [signatureCanvasSize, setSignatureCanvasSize] = useState({
      width: 0,
      height,
    });

    useImperativeHandle(ref, () => ({
      isEmpty: () => sigCanvasRef.current?.isEmpty(),
      toDataURL: (type, encoderOptions) =>
        sigCanvasRef.current?.toDataURL(type, encoderOptions),
      clear: () => sigCanvasRef.current?.clear(),
      getCanvas: () => sigCanvasRef.current?.getCanvas(),
    }));

    const updateSignatureCanvasSize = () => {
      const container = signatureContainerRef.current;
      if (!container) return;

      const width = Math.floor(container.getBoundingClientRect().width);
      if (!width || width <= 0) return;

      setSignatureCanvasSize((previous) => {
        if (previous.width === width && previous.height === height) {
          return previous;
        }
        return { width, height };
      });
    };

    useLayoutEffect(() => {
      const frame = requestAnimationFrame(() => {
        updateSignatureCanvasSize();
      });
      return () => cancelAnimationFrame(frame);
    }, [height]);

    useEffect(() => {
      const container = signatureContainerRef.current;
      if (!container) return;

      const observer = new ResizeObserver(() => {
        const signaturePad = sigCanvasRef.current;
        if (signaturePad && typeof signaturePad.isEmpty === "function" && !signaturePad.isEmpty()) {
          return; // Do not resize if already signed
        }
        updateSignatureCanvasSize();
      });

      observer.observe(container);
      return () => {
        observer.disconnect();
      };
    }, []);

    return (
      <div
        ref={signatureContainerRef}
        className={`w-full overflow-hidden ${className}`}
      >
        {signatureCanvasSize.width > 0 && (
          <SignatureCanvas
            ref={sigCanvasRef}
            penColor={penColor}
            canvasProps={{
              width: signatureCanvasSize.width,
              height: signatureCanvasSize.height,
              className: `block max-w-full touch-none bg-white ${canvasClassName}`,
            }}
          />
        )}
      </div>
    );
  }
);

export default SafetyResponsiveSignatureCanvas;
